/**
 * useNotifications Hook
 * Manages notification state with real-time WebSocket updates
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationService } from '../../services/api/notificationService';
import { useRealtimeEvent } from './useRealtimeEvent';
import {
  RealtimeEventType,
  NotificationNewEvent,
  NotificationCountUpdatedEvent,
  NotificationReadEvent,
  NotificationDeletedEvent
} from '../../services/websocket';
import {
  Notification,
  NotificationBatch
} from '../../types/notification.types';

interface UseNotificationsOptions {
  pollInterval?: number; // milliseconds (fallback, not used with WebSocket)
  limit?: number;
  autoFetch?: boolean;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  fetchNotifications: (page?: number) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useNotifications = (
  options: UseNotificationsOptions = {}
): UseNotificationsReturn => {
  const { limit = 20, autoFetch = true } = options;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchNotifications = useCallback(
    async (page: number = 1) => {
      setLoading(true);
      setError(null);

      try {
        const response = (await notificationService.getNotifications({
          page,
          limit
        })) as any;
        const batch = response.data || response; // Handle both wrapped and unwrapped responses

        if (page === 1) {
          setNotifications(batch.notifications || []);
        } else {
          setNotifications((prev) => [...prev, ...(batch.notifications || [])]);
        }

        setUnreadCount((prev) => batch.unreadCount != null ? batch.unreadCount : prev);
        setHasMore(batch.hasMore || false);
        setCurrentPage(page);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to fetch notifications')
        );
      } finally {
        setLoading(false);
      }
    },
    [limit]
  );

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = (await notificationService.getUnreadCount()) as any;
      const countData = response.data || response; // Handle both wrapped and unwrapped responses
      setUnreadCount(countData.count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);

      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      throw err;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      throw err;
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);

      setNotifications((prev) => {
        const notification = prev.find((n) => n._id === notificationId);
        const newNotifications = prev.filter((n) => n._id !== notificationId);

        if (notification && !notification.read) {
          setUnreadCount((count) => Math.max(0, count - 1));
        }

        return newNotifications;
      });
    } catch (err) {
      console.error('Failed to delete notification:', err);
      throw err;
    }
  }, []);

  const refresh = useCallback(() => {
    return fetchNotifications(1);
  }, [fetchNotifications]);

  // ===============================
  // REAL-TIME WEBSOCKET HANDLERS
  // ===============================

  // Handle new notification event
  useRealtimeEvent(
    RealtimeEventType.NOTIFICATION_NEW,
    useCallback((data: NotificationNewEvent) => {
      console.log('Real-time: New notification received', data);

      // Add new notification to the list
      const newNotification: Notification = {
        _id: data.notificationId,
        userId: '', // Will be current user
        type: data.type as any, // Type from backend may differ from frontend enum
        title: data.title,
        message: data.message,
        metadata: {
          actorId: data.actorId,
          actorName: data.actorUsername,
          entityId: data.tripId || data.destinationId,
          entityType: data.tripId ? 'trip' : 'destination'
        },
        read: false,
        createdAt:
          typeof data.createdAt === 'string'
            ? data.createdAt
            : new Date(data.createdAt).toISOString(),
        updatedAt:
          typeof data.createdAt === 'string'
            ? data.createdAt
            : new Date(data.createdAt).toISOString()
      };

      setNotifications((prev) => [newNotification, ...prev]);
    }, [])
  );

  // Handle notification read event
  useRealtimeEvent(
    RealtimeEventType.NOTIFICATION_READ,
    useCallback((data: NotificationReadEvent) => {
      console.log('Real-time: Notification marked as read', data);

      setNotifications((prev) =>
        prev.map((n) =>
          n._id === data.notificationId ? { ...n, read: true } : n
        )
      );
    }, [])
  );

  // Handle notification deleted event
  useRealtimeEvent(
    RealtimeEventType.NOTIFICATION_DELETED,
    useCallback((data: NotificationDeletedEvent) => {
      console.log('Real-time: Notification deleted', data);

      setNotifications((prev) =>
        prev.filter((n) => n._id !== data.notificationId)
      );
    }, [])
  );

  // Handle notification count update event
  useRealtimeEvent(
    RealtimeEventType.NOTIFICATION_COUNT_UPDATED,
    useCallback((data: NotificationCountUpdatedEvent) => {
      console.log('Real-time: Notification count updated', data);

      setUnreadCount(data.count);
    }, [])
  );

  // ===============================
  // INITIAL FETCH
  // ===============================

  // Initial fetch
  useEffect(() => {
    if (autoFetch) {
      fetchNotifications(1);
      fetchUnreadCount(); // Fetch count immediately on mount
    }
  }, [autoFetch, fetchNotifications, fetchUnreadCount]);

  // No more polling - WebSocket handles real-time updates!

  return {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh
  };
};
